import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Actions, ofActionDispatched, Store } from '@ngxs/store';
import { isDefined, isNil } from '@trimble-gcs/common';
import { ClipAndSaveComponent } from '../clip-and-save/clip-and-save.component';
import { ClipBox } from '../connect-3d-ext/clip-box/clip-box.models';
import { ProjectQuotaService } from '../quota/project-quota.service';
import { PatchScandataModel } from '../scandata/scandata.actions';
import { ScandataModel } from '../scandata/scandata.models';
import { ClearCurrentStation } from '../station/station.actions';
import { Scan3dStyle } from './models/scan-3d-style';
import { Scan3dStyleRenderingMode } from './models/scan-3d-style-rendering-mode';
import { Scan3dDetailComponent } from './scan-3d-detail/scan-3d-detail.component';
import { Scan3dListComponent } from './scan-3d-list/scan-3d-list.component';
import { Scan3dSettingsComponent } from './scan-3d-settings/scan-3d-settings.component';
import { ElevationPicked } from './scan-3d.actions';
import { Scan3dState } from './scan-3d.state';

enum Scan3dView {
  List = 'List',
  Details = 'Details',
  Settings = 'Settings',
  ClipAndSave = 'ClipAndSave',
}

interface ClipAndSaveInput {
  pointcloudIds: string[];
  clipBox: ClipBox;
}

@UntilDestroy()
@Component({
  selector: 'sd-scan-3d-panel',
  imports: [
    CommonModule,
    Scan3dListComponent,
    Scan3dDetailComponent,
    Scan3dSettingsComponent,
    ClipAndSaveComponent,
  ],
  templateUrl: './scan-3d-panel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Scan3dPanelComponent {
  private readonly actions = inject(Actions);
  private readonly store = inject(Store);
  private readonly projectQuotaService = inject(ProjectQuotaService);

  readonly globalStyle = input<Scan3dStyle | undefined>();
  readonly elevationPicking = input<boolean>(false);

  readonly elevationPickingChange = output<boolean>();

  readonly scan3dStyleUom = this.store.selectSignal(Scan3dState.scan3dStyleUom);

  readonly currentView = signal<Scan3dView>(Scan3dView.List);
  readonly currentScan = signal<ScandataModel | null>(null);
  readonly scan3dStyle = signal<Scan3dStyle | undefined>(undefined);
  readonly clipAndSaveInput = signal<ClipAndSaveInput | null>(null);

  readonly listHidden = computed(() => this.currentView() !== this.scan3dView.List);

  readonly quotaExceeded = toSignal(this.projectQuotaService.quotaExceeded(), {
    initialValue: false,
  });

  readonly scan3dView = Scan3dView;

  constructor() {
    this.subscribeToElevationPicked();
  }

  onExitStationClick() {
    this.store.dispatch(new ClearCurrentStation());
  }

  onSettingsClick() {
    this.currentView.set(Scan3dView.Settings);
  }

  onExitSettingsClick() {
    this.currentView.set(Scan3dView.List);

    this.elevationPickingChange.emit(false);
  }

  onModelDetailClick(model: ScandataModel) {
    this.currentScan.set(model);
    this.scan3dStyle.set(model.scan3dStyle ?? this.getCopyOfStyle(this.globalStyle()));
    this.currentView.set(Scan3dView.Details);
  }

  onClipAndSaveClick(input: ClipAndSaveInput) {
    this.clipAndSaveInput.set(input);
    this.currentView.set(Scan3dView.ClipAndSave);
  }

  onExitClipAndSaveClick() {
    this.clipAndSaveInput.set(null);
    this.currentView.set(Scan3dView.List);
  }

  onClipSaved() {
    this.onExitClipAndSaveClick();
  }

  onExitDetailClick() {
    this.currentScan.set(null);
    this.currentView.set(Scan3dView.List);

    this.elevationPickingChange.emit(false);
  }

  onStyleChange(style: Scan3dStyle) {
    const scan = this.currentScan();
    const scan3dStyle = this.scan3dStyle();
    if (isNil(scan) || isNil(scan3dStyle)) return;

    // Turn eye dome lighting on when classification color is switched on
    const classificationShowing =
      scan3dStyle.renderingMode === Scan3dStyleRenderingMode.Classification;

    if (
      classificationShowing === false &&
      style.renderingMode === Scan3dStyleRenderingMode.Classification &&
      style.showEyeDomeLighting === false
    ) {
      style.showEyeDomeLighting = true;
    }

    this.updateScan3dStyle(scan, style);
  }

  onElevationPickingChange(picking: boolean) {
    this.elevationPickingChange.emit(picking);
  }

  private subscribeToElevationPicked() {
    this.actions
      .pipe(ofActionDispatched(ElevationPicked), untilDestroyed(this))
      .subscribe(({ elevationPicked }) => {
        const scan = this.currentScan();
        const scan3dStyle = this.scan3dStyle();
        if (isNil(scan) || isNil(scan3dStyle)) return;

        this.updateScan3dStyle(scan, { ...scan3dStyle, elevationReference: elevationPicked });
      });
  }

  private getCopyOfStyle(style?: Scan3dStyle): Scan3dStyle | undefined {
    return isDefined(style)
      ? { ...style, classificationSchemes: [...style.classificationSchemes].map((x) => ({ ...x })) }
      : undefined;
  }

  private updateScan3dStyle(scan: ScandataModel, style: Scan3dStyle) {
    this.scan3dStyle.set(style);
    scan.scan3dStyle = style;

    this.store.dispatch(
      new PatchScandataModel({
        id: scan.id,
        scan3dStyle: scan.scan3dStyle,
      }),
    );
  }
}
